import Analytics from '@withlocals/withlocals-analytics';
import { Translator } from 'Translator';
import classnames from 'classnames';
import ImageComponent from 'components/Image';
import LocalizedLink from 'components/LocalizedLink';
import LocalizedNoIndexLink from 'components/LocalizedNoIndexLink';
import StarRating from 'components/StarRating';
import isAfter from 'date-fns/isAfter';
import subMonths from 'date-fns/subMonths';
import React, { useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { WlWindow } from 'typings/globals/cordova';
import { generatePathnameWithLangCode } from 'utils/RouteHelper';
import format from 'utils/formatDate';
import { isWeb } from 'utils/platform';

declare const window: WlWindow;

const CSS = require('./Review.less');

const VISIBLE_OFFSET = {
  top: -200,
  left: -200,
  right: -200,
  bottom: -500,
};
const INTERVAL_DELAY = 300;

const AVATAR_OPTIONS = {
  xs: { width: 48, height: 48 },
  sm: { width: 56, height: 56 },
};

type ReviewProps = {
  guestName: string;
  guestPhoto: string;
  hostName: string;
  hostPhoto: string;
  created: Date;
  rating: number;
  scale: number;
  title: string;
  comment: string;
  experienceTitle: string;
  experienceUrl: string;
  className?: string;
  hostUrl?: string;
};

function getCleanGuestName(name: string) {
  return name?.split('@')?.[0] ?? name;
}

const Review = ({
  guestName,
  guestPhoto,
  hostName,
  hostPhoto,
  created,
  rating,
  scale,
  title,
  comment,
  experienceTitle,
  experienceUrl,
  className,
  hostUrl,
}: ReviewProps) => {
  const avatarOptions =
    window.innerWidth < 768 ? AVATAR_OPTIONS.xs : AVATAR_OPTIONS.sm;
  const [isVisible, setVisible] = useState(false);
  const handleWaypointEnter = (visible) => (visible ? setVisible(true) : null);

  const onExperienceUrlClick = () => {
    Analytics.track(
      window.currentPageCategory,
      'Review link click',
      experienceUrl,
    );
  };

  const cleanGuestName = getCleanGuestName(guestName);

  const showDateOfReviewAfter = subMonths(new Date(), 2);

  return (
    <div className={classnames(CSS.Container, { [className]: className })}>
      <div className={CSS.Header}>
        <div className={CSS.Avatars}>
          {!isVisible && (
            <>
              <VisibilitySensor
                partialVisibility
                intervalDelay={INTERVAL_DELAY}
                onChange={handleWaypointEnter}
                offset={VISIBLE_OFFSET}
              />
              <noscript>
                <ImageComponent
                  className={CSS.Avatar}
                  options={avatarOptions}
                  src={hostPhoto}
                />
                <ImageComponent
                  className={classnames(CSS.Avatar, CSS.AvatarSecond)}
                  options={avatarOptions}
                  src={guestPhoto}
                />
              </noscript>
            </>
          )}
          {isVisible && (
            <>
              <ImageComponent
                animate
                className={CSS.Avatar}
                width={avatarOptions.width}
                height={avatarOptions.height}
                options={avatarOptions}
                src={hostPhoto}
              />
              <ImageComponent
                animate
                className={classnames(CSS.Avatar, CSS.AvatarSecond)}
                width={avatarOptions.width}
                height={avatarOptions.height}
                options={avatarOptions}
                src={guestPhoto}
              />
            </>
          )}
        </div>
        <div className={CSS.Details}>
          <div className={CSS.GuestName}>
            <span>{cleanGuestName}</span>
          </div>
          <div className={CSS.About}>
            {Translator.translate(
              'Common.Review.aboutHost',
              {
                hostName: (
                  <LocalizedNoIndexLink
                    className={CSS.LocalLink}
                    to={`/host/${hostUrl}`}
                  >
                    {hostName}
                  </LocalizedNoIndexLink>
                ),
              },
              false,
              'About local {hostName}',
            )}
          </div>
          <div className={CSS.Rating}>
            <StarRating rating={rating} scale={scale} />
            {isAfter(created, showDateOfReviewAfter) && (
              <span className={CSS.Created}>{format(created, 'LL')}</span>
            )}
          </div>
        </div>
      </div>
      <div className={CSS.Content}>
        <span className={CSS.Title}>{title}</span>
        <p className={CSS.Paragraph}>{comment}</p>
      </div>
      <div className={CSS.Footer}>
        {!!experienceUrl ? (
          <LocalizedLink
            onClick={onExperienceUrlClick}
            to={generatePathnameWithLangCode(
              `/experience/${experienceUrl}`,
              Translator.getLanguageCode(),
            )}
            className={CSS.ExperienceLink}
            target={isWeb() && window.innerWidth >= 768 ? '_blank' : undefined}
          >
            {experienceTitle}
          </LocalizedLink>
        ) : (
          <span onClick={onExperienceUrlClick} className={CSS.ExperienceLink}>
            {experienceTitle}
          </span>
        )}
      </div>
    </div>
  );
};

export default Review;
